import { ApiError } from "@/lib/api/api-client";
import { getToken } from "@/lib/api/auth";
import type { AnalysisResult } from "@/lib/music/analyzer-types";

import {
  readJsonLines,
  readManifest,
  readNotesJournal,
  type ReplayManifest,
  type ReplayPacket,
  type ReplayParticipantLine,
  type ReplayStateLine,
} from "./replay-journal";
/**
 * Va chercher un replay : sa fiche, son manifeste, ses journaux.
 *
 * # Rien ne vient du seau directement
 *
 * Les fichiers sont dans le stockage objet, dont seul l'enregistreur a les
 * clés ; Symfony décide qui peut les lire et relaie les octets
 * (`RecitalReplayFileController`). Le lecteur ne connaît donc qu'une adresse,
 * la nôtre, avec le jeton du compte quand il y en a un.
 *
 * # Tout est chargé avant de commencer
 *
 * Les gestes d'une heure de récital pèsent moins d'un mégaoctet : les prendre
 * d'un coup est ce qui permet d'en faire un morceau, plein dès la première
 * image (`replay-song.ts`). Les images, elles, ne passent jamais par là — ce
 * sont des WebM navigables, qu'un `<video>` ouvre à la demande.
 */

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL ?? "";

/** La fiche d'un replay, telle que l'API la rend. */
export interface ReplayDescription {
  id: string;
  /**
   * Le nom de l'enregistrement, repli compris (son nom propre, sinon le titre
   * du récital) : celui que prend une prise qui n'a pas le sien. Absent d'une
   * API d'avant ce champ.
   */
  title?: string;
  recital: { id: string; title: string };
  startedAt: string;
  endedAt: string;
  durationMs: number;
  packets: number;
  tracks: number;
  degraded: boolean;
  published: boolean;
  mine: boolean;
  /**
   * Les prises telles que l'API les connaît : leur rang, leur durée, et si
   * elles sont publiées. Vide quand l'enregistrement n'a qu'une traversée.
   *
   * L'interprète les voit toutes ; un visiteur ne reçoit que les publiées —
   * lister les essais gardés pour soi reviendrait à les annoncer.
   */
  takes?: ReplayApiTake[];
  /**
   * Ce que filmait chaque caméra, par identité de qui la publiait, là où
   * l'interprète l'a corrigé après coup — `hands` ou `face`. À relire avec
   * `readCorrectedSubjects` : c'est une réponse de réseau. Absent d'une API
   * d'avant la correction.
   */
  cameras?: unknown;
  /** Le chemin des fichiers, barre finale comprise. */
  files: string;
  /**
   * Le droit d'ouvrir ces fichiers, dans l'adresse : un `<video src="…">` ne
   * porte aucun en-tête, donc pas le jeton du compte. Voir `ReplayLink` côté
   * API.
   */
  token: string;
  /**
   * L'instant où ce droit tombe, en millisecondes Unix. Le lecteur le
   * renouvelle avant (`linksExpiring`) : quelqu'un laisse la page ouverte la
   * journée et revient le soir. Absent d'une API d'avant : on ne renouvelle
   * rien, comme avant.
   */
  tokenExpiresAtMs?: number;
}

/**
 * Avant son échéance, de combien on renouvelle le droit de lire les fichiers,
 * en millisecondes.
 *
 * Dix minutes : assez pour qu'une vidéo lancée juste avant ne tombe pas au
 * milieu, assez peu pour ne pas renouveler pour rien. Renouveler change
 * l'adresse des vidéos, donc les recharge : on ne le fait qu'au besoin.
 */
export const RENEW_MARGIN_MS = 10 * 60_000;

/**
 * Le droit de lire les fichiers tombe, ou tombera avant qu'on s'en resserve :
 * il faut le renouveler.
 *
 * Avec la lecture directe, l'adresse présignée du seau vaut ce qui restait au
 * jeton qui l'a obtenue (`RecitalReplayFileController`) : les deux tombent
 * ensemble, et c'est ce seul instant qu'on regarde.
 */
export function linksExpiring(description: Pick<ReplayDescription, "tokenExpiresAtMs">, nowMs: number): boolean {
  const expiresAt = description.tokenExpiresAtMs;

  return expiresAt !== undefined && expiresAt > 0 && nowMs >= expiresAt - RENEW_MARGIN_MS;
}

/** Une prise, telle que l'API la décrit. */
export interface ReplayApiTake {
  /** Ce que porte le lien d'une prise partagée : le rang, lui, ne sort pas. */
  id: string;
  number: number;
  /** Son nom propre, `null` sans — elle prend alors celui de l'enregistrement. */
  title?: string | null;
  /** Il y a un MIDI, donc une analyse à demander. Faux pour une prise sans note. */
  analyzable?: boolean;
  /** Elle est déjà calculée : la demander ne coûtera rien. */
  analyzed?: boolean;
  startedAtMs: number;
  endedAtMs: number;
  durationMs: number;
  startPositionMs: number | null;
  endPositionMs: number | null;
  notes: number | null;
  published: boolean;
}

/**
 * L'adresse d'un fichier du replay, jeton compris.
 *
 * Pour un `<video>` : les journaux, eux, passent par `fetch` et n'en ont pas
 * besoin — ils portent le jeton du compte dans leur en-tête.
 */
export function replayFileUrl(description: ReplayDescription, file: string): string {
  return `${API_BASE_URL}${description.files}${file}?t=${encodeURIComponent(description.token)}`;
}

/** La fiche du replay d'un récital. Lève une `ApiError` s'il n'y en a pas. */
export async function fetchReplay(recitalId: string, signal?: AbortSignal): Promise<ReplayDescription> {
  const response = await get(`/api/live/recitals/${recitalId}/replay`, signal);

  return (await response.json()) as ReplayDescription;
}

/**
 * L'analyse d'une prise : sections, phrases, accords, tonalité.
 *
 * L'enregistreur écrit un MIDI par prise au dépôt ; c'est l'API qui le fait
 * analyser, à la demande et une seule fois (`docs/spec/recorder.md`). Le
 * document rendu est celui de l'analyseur, tel quel — la même forme qu'une
 * pièce de la bibliothèque, donc la même porte de chargement.
 *
 * Rend `null` plutôt que de lever quand l'analyse n'est pas là : un replay se
 * lit très bien sans elle, avec les notes rebâties depuis les journaux. Ce
 * qu'on perd, ce sont les sections et la tonalité — pas la musique.
 */
export async function fetchTakeAnalysis(
  recitalId: string,
  take: number,
  signal?: AbortSignal,
): Promise<AnalysisResult | null> {
  try {
    const response = await get(`/api/live/recitals/${recitalId}/replay/takes/${take}/analysis`, signal);

    return (await response.json()) as AnalysisResult;
  } catch (cause) {
    if (signal?.aborted === true) {
      throw cause;
    }

    console.warn("Analyse d'une prise :", cause);

    return null;
  }
}

/** Le manifeste : ce qu'un lecteur ouvre d'abord. */
export async function fetchManifest(
  description: ReplayDescription,
  signal?: AbortSignal,
): Promise<ReplayManifest> {
  const response = await get(`${description.files}manifest.json`, signal);

  return readManifest(await response.json());
}

/** Ce qu'un enregistrement a écrit, mis bout à bout. */
export interface ReplayJournals {
  packets: ReplayPacket[];
  states: ReplayStateLine[];
  participants: ReplayParticipantLine[];
}

/**
 * Charge les journaux de toutes les jointures et les met bout à bout.
 *
 * Une jointure est une entrée de l'enregistreur dans la salle : une liaison
 * perdue puis retrouvée en ouvre une autre, et rien n'est enregistré entre les
 * deux. Les gestes portent leur tick, donc le trou se traverse tout seul.
 */
export async function loadJournals(
  description: ReplayDescription,
  manifest: ReplayManifest,
  signal?: AbortSignal,
): Promise<ReplayJournals> {
  const packets: ReplayPacket[] = [];
  const states: ReplayStateLine[] = [];
  const participants: ReplayParticipantLine[] = [];

  /*
    Les gestes viennent des prises quand le dépôt est découpé (format 3), de la
    jointure sinon.

    Un dépôt découpé n'a plus de journal de jointure : il portait tous les
    essais, publiés ou non, et le donner à qui ouvre le lien d'une prise
    partagée, c'était lui donner les autres. Chaque prise a le sien, et on ne
    charge que ceux que le manifeste nomme — lequel est filtré, côté API, par ce
    qu'on a le droit de voir.
  */
  const perTake = (manifest.takes ?? []).flatMap((take) => take.notes ?? []);

  for (const file of perTake) {
    packets.push(...readNotesJournal(await bytes(`${description.files}${file}`, signal)));
  }

  for (const part of manifest.parts) {
    const [notes, state, roster] = await Promise.all([
      part.notes !== undefined && part.notes !== "" && part.packets > 0
        ? bytes(`${description.files}${part.notes}`, signal)
        : null,
      part.state === "" ? null : text(`${description.files}${part.state}`, signal),
      part.participants === "" ? null : text(`${description.files}${part.participants}`, signal),
    ]);

    if (notes !== null) {
      packets.push(...readNotesJournal(notes));
    }

    if (state !== null) {
      states.push(...readJsonLines<ReplayStateLine>(state));
    }

    if (roster !== null) {
      participants.push(...readJsonLines<ReplayParticipantLine>(roster));
    }
  }

  packets.sort((a, b) => a.receivedAtMs - b.receivedAtMs);
  states.sort((a, b) => a.receivedAtMs - b.receivedAtMs);
  participants.sort((a, b) => a.atMs - b.atMs);

  return { packets, states, participants };
}

async function bytes(path: string, signal?: AbortSignal): Promise<ArrayBuffer> {
  return await (await get(path, signal)).arrayBuffer();
}

async function text(path: string, signal?: AbortSignal): Promise<string> {
  return await (await get(path, signal)).text();
}

/** La requête, avec le jeton s'il existe. Lève une `ApiError` sur tout refus. */
async function get(path: string, signal?: AbortSignal): Promise<Response> {
  const token = getToken();

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "GET",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    signal,
  });

  if (!response.ok) {
    throw new ApiError(await describe(response), response.status);
  }

  return response;
}

/** Le message de l'API, ou un mot à nous quand elle n'en donne pas. */
async function describe(response: Response): Promise<string> {
  try {
    const body: unknown = await response.json();

    if (typeof body === "object" && body !== null && typeof (body as { error?: unknown }).error === "string") {
      return (body as { error: string }).error;
    }
  } catch {
    // Pas de JSON : le statut parlera pour lui.
  }

  return response.status === 404 ? "Ce récital n'a pas de replay." : "Le replay n'a pas pu être ouvert.";
}
